import { DialogTitle } from '@headlessui/react'
import { ArchiveBoxIcon, ArrowUturnLeftIcon } from '@heroicons/react/24/outline'
import { CheckIcon } from '@heroicons/react/24/solid'
import clsx from 'clsx'
import { useFormik } from 'formik'
import PropTypes from 'prop-types'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

import { getSmartPredictStates } from '../../../constants/smart-predict'
import {
  DEFAULT_STATUS_COLOR,
  MAX_DESCRIPTION_LENGTH,
  MAX_LABEL_LENGTH,
  STATUS_COLORS,
  SUPPORTED_LANGUAGES,
  normalizeLabelForCompare
} from '../../../helpers/talent-statuses'
import Input from '../../input'
import State from '../../state'
import Button from '../../tailwind/Button'
import Modal from '../../tailwind/Modal'

const I18N_PREFIX = 'components.settings.talent_statuses.modal.'
const PARENT_PREFIX = 'components.settings.talent_statuses.'

// Anführungszeichen und Zeilenumbrüche würden CSV-Export und Filter-Chips
// stören.
const FORBIDDEN_CHARACTERS = /^[^"\n\r]*$/

const OPEN_LABEL_KEY = 'constants.smart_predict.state_open_label'

/**
 * Anlegen und Bearbeiten eines eigenen Talent-Status. Bezeichnung auf Deutsch
 * ist Pflicht, Englisch optional. Die Bezeichnung muss sich von den Aivy-Status
 * (in beiden Sprachen) und von allen eigenen Status unterscheiden, auch von
 * archivierten, sonst kollidiert eine spätere Reaktivierung.
 */
const ModalEditTalentStatus = ({
  open,
  setOpen,
  definition,
  existing,
  onSubmit,
  onToggleArchived,
  restoreDisabled
}) => {
  const { t, i18n } = useTranslation()

  const isEdit = !!definition
  const [showTranslation, setShowTranslation] = useState(
    !!(definition?.label?.en || definition?.description?.en)
  )
  const [isToggling, setIsToggling] = useState(false)

  // Archivieren bzw. Reaktivieren als sekundäre Aktion im Dialog, analog zum
  // Entfernen in der Teamverwaltung. Danach schließt der Dialog.
  const handleToggleArchived = async () => {
    if (!definition || !onToggleArchived) return

    setIsToggling(true)

    try {
      await onToggleArchived(definition)
      setOpen(false)
    } catch (err) {
      console.error(err)
    } finally {
      setIsToggling(false)
    }
  }

  const ToggleIcon = definition?.archived ? ArrowUturnLeftIcon : ArchiveBoxIcon
  const toggleDisabled = isToggling || (definition?.archived && restoreDisabled)

  // Reservierte Bezeichnungen: Aivy-Status in allen Sprachen plus „Offen" und
  // die Bezeichnungen der anderen eigenen Status.
  const reservedLabels = useMemo(() => {
    const result = new Set()

    SUPPORTED_LANGUAGES.forEach((lang) => {
      const fixedT = i18n.getFixedT(lang)

      getSmartPredictStates().forEach(({ label }) => {
        result.add(normalizeLabelForCompare(fixedT(label)))
      })
      result.add(normalizeLabelForCompare(fixedT(OPEN_LABEL_KEY)))
    })

    existing
      .filter((item) => item.id !== definition?.id)
      .forEach(({ label }) => {
        Object.values(label).forEach((value) =>
          result.add(normalizeLabelForCompare(value))
        )
      })

    return result
  }, [i18n, existing, definition])

  const isAvailable = (value) =>
    !value || !reservedLabels.has(normalizeLabelForCompare(value))

  const labelSchema = Yup.string()
    .trim()
    .max(MAX_LABEL_LENGTH, t(I18N_PREFIX + 'label_too_long_validation'))
    .matches(
      FORBIDDEN_CHARACTERS,
      t(I18N_PREFIX + 'label_characters_validation')
    )
    .test(
      'available',
      t(I18N_PREFIX + 'label_duplicate_validation'),
      isAvailable
    )

  const descriptionSchema = Yup.string()
    .trim()
    .max(
      MAX_DESCRIPTION_LENGTH,
      t(I18N_PREFIX + 'description_too_long_validation')
    )
    .matches(
      FORBIDDEN_CHARACTERS,
      t(I18N_PREFIX + 'label_characters_validation')
    )

  const formik = useFormik({
    initialValues: {
      label_de: definition?.label?.de || '',
      label_en: definition?.label?.en || '',
      description_de: definition?.description?.de || '',
      description_en: definition?.description?.en || '',
      color: definition?.color || DEFAULT_STATUS_COLOR
    },
    validationSchema: Yup.object({
      label_de: labelSchema.required(
        t(I18N_PREFIX + 'label_required_validation')
      ),
      label_en: labelSchema,
      description_de: descriptionSchema,
      description_en: descriptionSchema,
      color: Yup.string().oneOf(Object.keys(STATUS_COLORS))
    }),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)

      try {
        await onSubmit(values, definition?.id)
        setOpen(false)
      } catch (err) {
        console.error(err)
      } finally {
        setSubmitting(false)
      }
    }
  })

  const previewText =
    formik.values.label_de.trim() || t(I18N_PREFIX + 'preview_placeholder')

  const renderInput = (id, { optional, placeholder } = {}) => (
    <Input
      id={id}
      label={t(I18N_PREFIX + `${id}_label`)}
      placeholder={placeholder}
      optional={optional}
      type='text'
      autoComplete='off'
      onChange={formik.handleChange}
      onBlur={formik.handleBlur}
      touched={formik.touched[id]}
      value={formik.values[id]}
      error={formik.errors[id]}
    />
  )

  return (
    <Modal open={open} setOpen={() => setOpen(false)} size='lg'>
      <div className='mt-4 text-center'>
        <DialogTitle
          as='h3'
          className='text-lg font-medium leading-6 text-gray-900'
        >
          {t(I18N_PREFIX + (isEdit ? 'title_edit' : 'title_create'))}
        </DialogTitle>
        <p className='mx-auto mt-1 max-w-sm text-sm text-gray-500'>
          {t(I18N_PREFIX + 'description')}
        </p>
      </div>

      <div className='mt-6 flex items-center justify-center rounded-lg bg-gray-50 py-4'>
        <State text={previewText} color={formik.values.color} height={28} />
      </div>

      <div className='mt-6 flex flex-col gap-y-4'>
        {renderInput('label_de', {
          placeholder: t(I18N_PREFIX + 'label_de_placeholder')
        })}
        {renderInput('description_de', {
          optional: true,
          placeholder: t(I18N_PREFIX + 'description_de_placeholder')
        })}

        <div>
          <span className='block text-sm font-medium text-gray-700'>
            {t(I18N_PREFIX + 'color_label')}
          </span>
          <div className='mt-2 flex flex-wrap gap-2' role='radiogroup'>
            {Object.keys(STATUS_COLORS).map((color) => {
              const selected = formik.values.color === color

              return (
                <button
                  key={color}
                  type='button'
                  role='radio'
                  aria-checked={selected}
                  aria-label={t(I18N_PREFIX + `color_${color}`)}
                  title={t(I18N_PREFIX + `color_${color}`)}
                  onClick={() => formik.setFieldValue('color', color)}
                  className={clsx(
                    'flex size-8 items-center justify-center rounded-full ring-offset-2 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600',
                    STATUS_COLORS[color].dot,
                    selected && 'ring-2 ring-gray-900'
                  )}
                >
                  {selected && (
                    <CheckIcon
                      className='size-4 text-white'
                      aria-hidden='true'
                    />
                  )}
                </button>
              )
            })}
          </div>
        </div>

        {showTranslation ? (
          <div className='flex flex-col gap-y-4 rounded-lg border border-gray-200 p-4'>
            <span className='text-[11px] font-bold uppercase tracking-wide text-gray-400'>
              {t(I18N_PREFIX + 'translation_group')}
            </span>
            {renderInput('label_en', {
              optional: true,
              placeholder: t(I18N_PREFIX + 'label_en_placeholder')
            })}
            {renderInput('description_en', {
              optional: true,
              placeholder: t(I18N_PREFIX + 'description_en_placeholder')
            })}
          </div>
        ) : (
          <button
            type='button'
            onClick={() => setShowTranslation(true)}
            className='self-start text-sm font-medium text-primaryBlue hover:text-primaryBlue/80'
          >
            {t(I18N_PREFIX + 'translation_add_action')}
          </button>
        )}
      </div>

      {isEdit && onToggleArchived && (
        <div className='mt-8 flex justify-end'>
          <button
            type='button'
            onClick={handleToggleArchived}
            disabled={toggleDisabled || formik.isSubmitting}
            className='group inline-flex items-center gap-x-1.5 text-sm font-semibold text-gray-600 hover:text-gray-900 disabled:cursor-default disabled:opacity-40'
          >
            {t(
              PARENT_PREFIX +
                (definition.archived ? 'restore_action' : 'archive_action')
            )}
            <ToggleIcon
              className='h-5 w-5 text-gray-500 group-hover:text-gray-700'
              aria-hidden='true'
            />
          </button>
        </div>
      )}

      <div className='mt-10 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3'>
        <Button.SecondaryLG
          text={t(I18N_PREFIX + 'cancel_action')}
          onClick={() => setOpen(false)}
          disabled={formik.isSubmitting || isToggling}
        />
        <Button.PrimaryLG
          text={t(I18N_PREFIX + (isEdit ? 'save_action' : 'create_action'))}
          onClick={formik.handleSubmit}
          isLoading={formik.isSubmitting}
          disabled={isToggling}
        />
      </div>
    </Modal>
  )
}

ModalEditTalentStatus.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  definition: PropTypes.object,
  existing: PropTypes.array,
  onSubmit: PropTypes.func.isRequired,
  onToggleArchived: PropTypes.func,
  restoreDisabled: PropTypes.bool
}

ModalEditTalentStatus.defaultProps = {
  definition: null,
  existing: [],
  onToggleArchived: undefined,
  restoreDisabled: false
}

export default ModalEditTalentStatus
